import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CHART_TOOLTIP_HIDE_DELAY } from './chart.constant';
import type { ChartTooltipPlacement } from './chart-tooltip.ui';
import type { ChartTooltipContent } from './model/chart-tooltip.types';

/** 떠 있는 말풍선 하나: 내용 + 놓을 자리(화면 좌표) */
export interface ChartTooltipState {
  /** 무엇에 올렸는지(같은 요소면 다시 그리지 않는 데 쓴다) */
  key: string;
  content: ChartTooltipContent;
  anchor: { x: number; y: number };
  placement?: ChartTooltipPlacement;
}

/**
 * 그래프의 말풍선 상태. 요소에서 벗어나면 바로 닫지 않고 잠깐(120ms) 기다린다 —
 * 그 사이 마우스가 말풍선으로 옮겨 가면(`bubbleProps`) 닫지 않아 말풍선 안을 스크롤할 수 있다(규격 §4).
 */
export function useChartTooltip() {
  const [tooltip, setTooltip] = useState<ChartTooltipState | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const cancelHide = useCallback(() => {
    if (timer.current !== undefined) clearTimeout(timer.current);
    timer.current = undefined;
  }, []);

  const show = useCallback(
    (next: ChartTooltipState) => {
      cancelHide();
      setTooltip(next);
    },
    [cancelHide],
  );

  const hide = useCallback(() => {
    cancelHide();
    timer.current = setTimeout(() => {
      timer.current = undefined;
      setTooltip(null);
    }, CHART_TOOLTIP_HIDE_DELAY);
  }, [cancelHide]);

  const hideNow = useCallback(() => {
    cancelHide();
    setTooltip(null);
  }, [cancelHide]);

  useEffect(() => cancelHide, [cancelHide]);

  const bubbleProps = useMemo(
    () => ({ onPointerEnter: cancelHide, onPointerLeave: hide }),
    [cancelHide, hide],
  );

  return { tooltip, show, hide, hideNow, cancelHide, bubbleProps };
}
